import React, { PropsWithChildren, useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Engine, EngineResult } from 'json-rules-engine';
import { CustomOperators } from '@trnsact/business-criteria';
import { ProposalProduct } from '@trnsact/trnsact-shared-types/dist/generated';
import { deskingActions } from '../../model';
import { Criteria, FailedRule } from '../../types';
import { evaluateCondition, groupFailedConditions } from '../../lib/compareJSONRule';
import { jsonRulesEngineContext as JsonRulesEngineContext } from './jsonRulesEngineContext';

export const RulesEngineProvider = ({ children }: PropsWithChildren) => {
  const [failedRules, setFailedRules] = useState<FailedRule[]>([]);

  const dispatch = useDispatch();

  const productConfigurationRulesEngine = useRef<Engine>(new Engine());

  productConfigurationRulesEngine.current.addOperator('length', CustomOperators.length);

  const handleAddProductsRules = useCallback((product: ProposalProduct) => {
    const rules = (product.aftermarketProduct?.criteria ?? []) as Criteria[];

    rules.forEach(rule => {
      productConfigurationRulesEngine.current.addRule({
        ...rule,
        conditions: { all: rule.conditions.jsonRules.conditions.all },
      });
    });
  }, []);

  const handleRunEngin = useCallback(() => {
    dispatch(deskingActions.runJsonEngine());
  }, [dispatch]);

  const analyzeFailureResults = useCallback((engineResults: EngineResult) => {
    const firstFailedRule = engineResults.failureResults[0];

    if (!firstFailedRule?.conditions) return;

    const failedConditions = (firstFailedRule.conditions as any)?.all ?? [];

    const failedExplanationsMap: Record<string, Omit<FailedRule, 'humanReadableReason' | 'fact'>[]> = {};

    for (const condition of failedConditions) {
      const { fact: factName, value: expected, operator } = condition;
      const actual = (productConfigurationRulesEngine.current.getFact(factName).value ?? 'undefined') as string;

      const passed = evaluateCondition(operator, actual, expected);

      if (actual !== expected && !passed) {
        if (!failedExplanationsMap[factName]) failedExplanationsMap[factName] = [];
        failedExplanationsMap[factName].push({ operator, expected, actual });
      }
    }

    setFailedRules(groupFailedConditions(failedExplanationsMap));
  }, []);

  return (
    <JsonRulesEngineContext.Provider
      value={{
        failedRules,
        analyzeFailureResults,
        handleRunEngin,
        handleAddProductsRules,
        engine: productConfigurationRulesEngine.current,
      }}
    >
      {children}
    </JsonRulesEngineContext.Provider>
  );
};
